import { Component, ElementRef, viewChild, input, effect, HostListener } from '@angular/core';
import { axisBottom, axisLeft, NumberValue, scaleBand, scaleLinear, select } from 'd3';
import { formatCost, formatDropdownValue, getEnumFromString } from '../../../helpers/kpiWidget/kpi-formatters.helper';
import { MeasurementTypes } from '../../../enums/kpiStats/measurement-types.enum';

@Component({
	selector: 'app-grouped-bar-chart',
	standalone: true,
	imports: [],
	templateUrl: './grouped-bar-chart.component.html',
	styleUrl: './grouped-bar-chart.component.scss'
})
export class GroupedBarChartComponent {
	readonly chartContainer = viewChild<ElementRef>('multipleBarChart');

	exportBtn = input(false);
	public data = input.required<any[]>();
	public margin = input({ top: 31, right: 10, bottom: 20, left: 0 });
	public viewMode = input<'all' | 'unusual' | 'driving' | 'idling'>('all');
	public yTicks = input<number[]>([50, 40, 30, 20, 10, 0]);
	public barWidth = input.required<number>();
	public chartHeight = input.required<number>();
	public labelKey = input<string>('label');
	public subgroups = input<Array<{ name: string, legend: string, color: string }>>([
		{ name: 'Unusual', legend: 'Unusual Loss', color: '#FF7F00' },
		{ name: 'Driving', legend: 'Driving', color: '#3873C9' },
	]);

	public tooltipUnit = input<string>('ℓ');

	// When true, only renders bars with non-zero values (for threshold-based charts)
	public skipZeroBars = input<boolean>(false);

	constructor() {
		effect(() => {
			if (this.data()?.length && this.chartContainer()?.nativeElement && this.margin() && this.viewMode() && this.yTicks() && this.barWidth() && this.chartHeight()) {
				this.createChart(
					this.data(),
					this.chartContainer()?.nativeElement,
					this.margin(),
					this.viewMode(),
					this.yTicks(),
					this.barWidth(),
					this.chartHeight(),
					this.labelKey()
				);
			}
		});
	}

	@HostListener('window:resize')
	onResize() {
		if (this.data()?.length && this.chartContainer()?.nativeElement && this.margin() && this.viewMode() && this.yTicks() && this.barWidth() && this.chartHeight()) {
			this.createChart(
				this.data(),
				this.chartContainer()?.nativeElement,
				this.margin(),
				this.viewMode(),
				this.yTicks(),
				this.barWidth(),
				this.chartHeight(),
				this.labelKey()
			);
		}
	}

	createChart(
		data: any[],
		element: HTMLElement,
		margin: any,
		viewMode: string,
		yTicks: number[],
		barWidth: number,
		fullHeight: number,
		labelKey: string
	) {
		const fullWidth = element.getBoundingClientRect().width;
		const width = fullWidth - margin.left - margin.right;
		const height = fullHeight - margin.top - margin.bottom;

		select(element).selectAll('svg').remove();

		const svg = select(element)
			.append('svg')
			.attr('width', fullWidth)
			.attr('height', fullHeight)
			.append('g')
			.attr('transform', `translate(${margin.left},${margin.top})`);

		let subgroupConfig = this.subgroups();
		if (viewMode !== 'all') {
			subgroupConfig = subgroupConfig.filter(s => s.name.toLowerCase().includes(viewMode));
		}

		const skipZeroBars = this.skipZeroBars();

		// For skipZeroBars mode, determine which subgroups actually have data
		let activeSubgroupNames: string[] = subgroupConfig.map(s => s.name);
		if (skipZeroBars) {
			// Find subgroups that have at least one non-zero value
			const subgroupsWithData = new Set<string>();
			data.forEach(d => {
				subgroupConfig.forEach(s => {
					if ((d[s.name] || 0) > 0) {
						subgroupsWithData.add(s.name);
					}
				});
			});
			activeSubgroupNames = subgroupConfig
				.filter(s => subgroupsWithData.has(s.name))
				.map(s => s.name);
		}

		const subgroupNames = subgroupConfig.map(s => s.name);
		const colorMap = new Map(subgroupConfig.map(s => [s.name, s.color]));

		const groups = data.map(d => d[labelKey]);
		const y = scaleLinear().domain([0, Math.max(...yTicks)]).range([height, 0]);

		const tmpAxis = axisLeft(y)
			.tickValues(yTicks)
			.tickFormat(d => this.formatValue(d))
			.tickSize(0);

		const tmp = svg.append('g').attr('class', 'yAxisTmp').call(tmpAxis);
		const maxLabelWidth = Math.ceil(
			Math.max(0, ...tmp.selectAll('text').nodes().map((n: any) => n.getBBox().width))
		);
		tmp.remove();

		const leftPad = maxLabelWidth + 10;
		const innerWidth = Math.max(0, width - leftPad);
		const plot = svg.append('g').attr('transform', `translate(${leftPad},0)`);

		const yAxis = axisLeft(y)
			.tickValues(yTicks)
			.tickFormat(d => this.formatValue(d))
			.tickSize(-innerWidth)
			.tickPadding(10);

		const yAxisGroup = plot.append('g').call(yAxis);
		yAxisGroup.select('.domain').remove();
		yAxisGroup.selectAll('line').attr('stroke', '#E0E0E0');
		yAxisGroup.selectAll('text').attr('text-anchor', 'end')
			.style('font-family', '"Nunito Sans", sans-serif !important')
			.style('fill', '#9FA1A4')
			.style('font-size', '10px')

		// For skipZeroBars, use per-row bar count; otherwise use all subgroups
		const getActiveSubgroupsForRow = (d: any): string[] => {
			if (skipZeroBars) {
				return subgroupNames.filter(name => (d[name] || 0) > 0);
			}
			return subgroupNames;
		};

		// Calculate group width based on max bars needed (for skipZeroBars, typically 2)
		const maxBarsPerGroup = skipZeroBars ? 2 : subgroupNames.length;
		const groupWidth = barWidth * maxBarsPerGroup;
		const spacing = groups.length > 1 ? (innerWidth - groups.length * groupWidth) / (groups.length - 1) : 0;
		const x0Map = new Map(groups.map((label, i) => [label, i * (groupWidth + spacing)]));
		const x0 = scaleBand().domain(groups).range([0, innerWidth]);
		const x1 = scaleBand().domain(subgroupNames).range([0, groupWidth]).padding(0);

		plot.append('g')
			.attr('transform', `translate(0,${height})`)
			.call(axisBottom(x0))
			.call(g => {
				g.selectAll('line').remove();
				g.select('.domain').remove();
				g.selectAll('g').each((d, i, nodes) => {
					const tick = select(nodes[i]);
					if (i !== 0 && i !== nodes.length - 1) {
						const gap = (innerWidth - (barWidth * 2) - ((nodes.length - 2) * barWidth)) / (nodes.length - 1);
						const space = (i * barWidth) + (i * gap) + (barWidth / 2);
						tick.attr('transform', `translate(${space},0)`)
					}
				})
				g.selectAll('text')
					.style('fill', '#9FA1A4')
					.style('font-family', '"Nunito Sans", sans-serif !important')
					.style('font-size', '10px')
					.each((d, i, nodes) => {
						const text = select(nodes[i]);
						if (i === 0) {
							// First tick: align left
							text.attr('text-anchor', 'start')
								.attr('x', -x0.bandwidth() / 2);
						} else if (i === nodes.length - 1) {
							// Last tick: align right
							text.attr('text-anchor', 'end')
								.attr('x', x0.bandwidth() / 2);
						} else {
							// Middle ticks: center
							text.attr('text-anchor', 'middle')
								.attr('x', 0);
						}
					});
			});

		const tooltip = select(element)
			.append('div')
			.style('opacity', 0)
			.style('position', 'absolute')
			.style('background', '#fff')
			.style('border-radius', '8px')
			.style('padding', '4px 6px')
			.style('box-shadow', '0 4px 10px rgba(0, 0, 0, 0.08)')
			.style('border', '1px solid #e0e0e0')
			.style('pointer-events', 'none')
			.style('font-family', '"Nunito Sans", sans-serif !important')
			.style('z-index', '999');

		const component = this;
		plot.append('g')
			.selectAll('g')
			.data(data)
			.enter()
			.append('g')
			.attr('transform', d => `translate(${x0Map.get(d[labelKey])},0)`)
			.selectAll('rect')
			.data(d => {
				if (skipZeroBars) {
					// Only include bars with non-zero values, position them sequentially
					const activeBars = subgroupNames
						.filter(name => (d[name] || 0) > 0)
						.map((name, idx) => ({ key: name, value: d[name], label: d[labelKey], barIndex: idx }));
					return activeBars;
				}
				return subgroupNames.map((name, idx) => ({ key: name, value: d[name], label: d[labelKey], barIndex: idx }));
			})
			.enter()
			.append('rect')
			.attr('x', d => skipZeroBars ? (d as any).barIndex * barWidth : x1(d.key)!)
			.attr('y', d => y(d.value))
			.attr('width', barWidth)
			.attr('height', d => height - y(d.value))
			.attr('fill', d => colorMap.get(d.key)!)
			.on('mouseover', function (event, d) {
				const current = data.find(item => item[labelKey] === d.label);
				tooltip
					.style('opacity', 1)
					.html(`
              <div style="font-weight: 600; font-size: 13px; color: #2B2B2B; margin-bottom: 1px;">
                ${current?.dateRange || d.label}
              </div>
              ${subgroupNames.map(name => `
                <div style="display: flex; align-items: center; margin-bottom: 1px; font-family: 'Nunito Sans', sans-serif;">
                  <div style="width: 12px; height: 12px; background: ${colorMap.get(name)}; border-radius: 50%; margin-right: 6px;"></div>
                  <div style="color: #9FA1A4; font-size: 12px; flex: 1; margin-right: 8px;">${name}</div>
                  <div style="color: #9FA1A4; font-size: 12px;">${component.formatToolTipValue(current, name, component)}</div>
                </div>`).join('')}`
					);
			})
			.on('mousemove', function (event) {
				const tooltipEl = tooltip.node() as HTMLDivElement;
				const tooltipWidth = tooltipEl.offsetWidth;
				const containerRect = element.getBoundingClientRect();

				if (containerRect.right - event.clientX < tooltipWidth) {
					tooltip
						.style('left', event.offsetX - tooltipWidth - 5 + 'px')
						.style('top', event.offsetY - 28 + 'px');
				} else {
					tooltip
						.style('left', event.offsetX + 10 + 'px')
						.style('top', event.offsetY - 28 + 'px');
				}
			})
			.on('mouseout', function () {
				select(this).attr('opacity', 1);
				tooltip.style('opacity', 0);
			});
	}

	private formatValue(data: NumberValue) {
		const isCurrency = this.tooltipUnit() !== 'ℓ' && this.tooltipUnit() !== '%';
		const isPercentage = this.tooltipUnit() === '%';
		return isCurrency
			? formatCost(data as number, this.tooltipUnit())
			: isPercentage ? `${formatDropdownValue(data as number, MeasurementTypes.Percentage.toString(), [], getEnumFromString)}` : formatDropdownValue(data as number, MeasurementTypes.Volume.toString(), [], getEnumFromString) + this.tooltipUnit();
	}

	private formatToolTipValue(current: any, name: string, component: this) {
		const rawValue = current?.[name] ?? 0;
		const formattedValue = Number(rawValue).toLocaleString();

		const isCurrency = component.tooltipUnit() !== 'ℓ' && component.tooltipUnit() !== '%';
		const isPercentage = component.tooltipUnit() === '%';
		return isCurrency
			? `${component.tooltipUnit()}${formattedValue}`
			: isPercentage ? `${formattedValue}${component.tooltipUnit()}` : `${formattedValue} ${component.tooltipUnit()}`;
	}
}